import React from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import CoronavirusOutlined from '@mui/icons-material/CoronavirusOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import MonitorWeightOutlined from '@mui/icons-material/MonitorWeightOutlined';
import VaccinesOutlined from '@mui/icons-material/VaccinesOutlined';
import BloodtypeOutlined from '@mui/icons-material/BloodtypeOutlined';
import AirOutlined from '@mui/icons-material/AirOutlined';

import { Condition } from '../../../../../../core/types/health.types';
import { languagePreferenceSelector } from '../../../../../../store/participant/selectors';
import { useAppSelector } from '../../../../../../store';

interface SimpleProps {
  data: Condition[];
  type: string;
  page?: number;
  rowsPerPage?: number;
}

const conditionColours = {
  hypertension: '#EA516D',
  retrovirus: '#12ACA2',
  diabetes: '#5ABBE5',
  cholesterol: '#F2AE21',
  obesity: '#92C15F',
  respiratory: '#7DC5E6',
  default: '#ED7723',
};

function getConditionPresentation(description: string) {
  const normalisedDescription = description.toLowerCase();

  if (normalisedDescription.includes('hypertension') || normalisedDescription.includes('blood pressure')) {
    return {
      colour: conditionColours.hypertension,
      Icon: MonitorHeartOutlined,
    };
  }

  if (
    normalisedDescription.includes('retrovirus') ||
    normalisedDescription.includes('hiv') ||
    normalisedDescription.includes('infection')
  ) {
    return {
      colour: conditionColours.retrovirus,
      Icon: CoronavirusOutlined,
    };
  }

  if (normalisedDescription.includes('diabetes')) {
    return {
      colour: conditionColours.diabetes,
      Icon: VaccinesOutlined,
    };
  }

  if (
    normalisedDescription.includes('hyperlipidaemia') ||
    normalisedDescription.includes('hyperlipidemia') ||
    normalisedDescription.includes('lipid') ||
    normalisedDescription.includes('cholesterol') ||
    normalisedDescription.includes('blood fats')
  ) {
    return {
      colour: conditionColours.cholesterol,
      Icon: BloodtypeOutlined,
    };
  }

  if (normalisedDescription.includes('obesity') || normalisedDescription.includes('weight')) {
    return {
      colour: conditionColours.obesity,
      Icon: MonitorWeightOutlined,
    };
  }

  if (
    normalisedDescription.includes('dyspnoea') ||
    normalisedDescription.includes('dyspnea') ||
    normalisedDescription.includes('shortness of breath') ||
    normalisedDescription.includes('breath')
  ) {
    return {
      colour: conditionColours.respiratory,
      Icon: AirOutlined,
    };
  }

  return {
    colour: conditionColours.default,
    Icon: HealthAndSafetyOutlined,
  };
}

function ConditionsList({ data, type, page = 0, rowsPerPage = 10 }: SimpleProps) {
  const languagePreference = useAppSelector(languagePreferenceSelector);
  const format = languagePreference === 'en' ? 'DD MMM YYYY' : 'DD/MM/YYYY';

  return (
    <>
      {data
        .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
        .map(
          (
            {
              effectiveDate,
              primary,
              ICDDescription,
              ICDHierLevel1,
              ICDHierLevel2,
              ICDHierLevel3,
              ICDHierLevel4,
            }: Condition,
            index: number,
          ) => {
            const { colour, Icon } = getConditionPresentation(ICDDescription || '');

            const hierarchyItems = [ICDHierLevel1, ICDHierLevel2, ICDHierLevel3, ICDHierLevel4].filter(Boolean);

            return (
              <Paper
                key={`${type}-${effectiveDate}-${ICDDescription}-${index}`}
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  transition: 'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
                  '&:hover': {
                    borderColor: colour,
                    boxShadow: `0 12px 30px ${colour}22`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${colour}18`,
                      color: colour,
                      border: 1,
                      borderColor: `${colour}44`,
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 30 }} />
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.75,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CalendarMonthOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />

                        <Typography variant="body2" component="p" color="text.secondary" sx={{ fontWeight: 800 }}>
                          {moment(effectiveDate).format(format)}
                        </Typography>
                      </Box>

                      {primary && (
                        <Chip
                          label="Primary"
                          size="small"
                          sx={{
                            height: 24,
                            fontWeight: 800,
                            bgcolor: `${colour}18`,
                            color: colour,
                            border: 1,
                            borderColor: `${colour}44`,
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 900,
                        lineHeight: 1.2,
                        color: 'text.primary',
                      }}
                    >
                      {ICDDescription}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                      <LocalHospitalOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />

                      <Typography
                        variant="body2"
                        component="p"
                        color="text.secondary"
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        Inkosi Albert Luthulu Central Hospital
                      </Typography>
                    </Box>

                    {hierarchyItems.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          pl: 1.5,
                          borderLeft: 3,
                          borderColor: `${colour}55`,
                          display: 'grid',
                          gap: 0.5,
                        }}
                      >
                        {hierarchyItems.map((item, hierarchyIndex) => (
                          <Typography
                            key={`${type}-${index}-hierarchy-${hierarchyIndex}`}
                            variant="body2"
                            component="p"
                            color="text.secondary"
                            sx={{
                              fontWeight: hierarchyIndex === 0 ? 800 : 600,
                              pl: hierarchyIndex * 1.5,
                              lineHeight: 1.35,
                            }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          },
        )}
    </>
  );
}

export default ConditionsList;
